import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { getLang } from '../../platform/lang';
import { reportError } from '../../core/telemetry/report';

/*
 * LOCATION IS REQUIRED (5 Oct), like installing the app.
 *
 * Owner: "it should be mandatory just like the test and home app install ...
 * if GPS is off they need to fix it to continue. We are having a lot of people
 * try to turn it off."
 *
 * WHAT LOCKS, AND WHAT DELIBERATELY DOES NOT. False negatives cost jobs:
 *   - Location SWITCHED OFF (the phone answers "denied") -> locked, always. On
 *     an iPhone that is both the app's permission and Location Services.
 *   - "Unavailable" -> locked ONLY outside a stop and only if the phone has had
 *     no location at all for 15 minutes. On Android that is what switching the
 *     phone's Location off looks like; but a basement can look the same, and a
 *     driver must never be locked out of a delivery they are standing in.
 *   - Slow / weak signal (timeout) -> never locked.
 * Checked when the screen opens, each time the app comes back on screen, when
 * the permission changes, and every 2 minutes. Every lock is recorded for the
 * office (Phone check-up -> "Switched off").
 */

type State = 'checking' | 'ok' | 'off';
let lastGoodFix = 0;

function ask(): Promise<'ok' | 'denied' | 'unavailable' | 'slow'> {
  return new Promise((resolve) => {
    if (!('geolocation' in navigator)) { resolve('denied'); return; }
    navigator.geolocation.getCurrentPosition(
      () => { lastGoodFix = Date.now(); resolve('ok'); },
      (err) => resolve(err.code === err.PERMISSION_DENIED ? 'denied'
        : err.code === err.POSITION_UNAVAILABLE ? 'unavailable' : 'slow'),
      { enableHighAccuracy: false, maximumAge: 60_000, timeout: 15_000 },
    );
  });
}

const inStop = () => window.location.pathname.startsWith('/stop');
const isIOS = () => /iphone|ipad|ipod|macintosh/i.test(navigator.userAgent) && (navigator.maxTouchPoints ?? 0) > 1
  || /iphone|ipad|ipod/i.test(navigator.userAgent);

export function LocationLock({ children }: { children: ReactNode }) {
  // Optimistic: the route shows at once; the lock appears only once the phone says location is off.
  const [state, setState] = useState<State>('ok');
  const [why, setWhy] = useState('');
  const lastReport = useRef(0);

  const check = useCallback(async () => {
    const r = await ask();
    const off = r === 'denied'
      || (r === 'unavailable' && !inStop() && Date.now() - lastGoodFix > 15 * 60_000);
    if (off) {
      setWhy(r);
      if (Date.now() - lastReport.current > 10 * 60_000) {
        lastReport.current = Date.now();
        reportError('diag_permission', 'location_lock', JSON.stringify({
          name: 'geolocation', to: 'locked', why: r, path: window.location.pathname }));
      }
      setState('off');
    } else {
      setState('ok');
    }
  }, []);

  useEffect(() => {
    void check();
    const onVis = () => { if (document.visibilityState === 'visible') void check(); };
    document.addEventListener('visibilitychange', onVis);
    const timer = setInterval(() => { if (document.visibilityState === 'visible') void check(); }, 120_000);
    let perm: PermissionStatus | null = null;
    const onPerm = () => void check();
    try {
      void navigator.permissions?.query({ name: 'geolocation' as PermissionName })
        .then((p) => { perm = p; p.addEventListener('change', onPerm); }).catch(() => {});
    } catch { /* no Permissions API: the other triggers still run */ }
    return () => {
      document.removeEventListener('visibilitychange', onVis);
      clearInterval(timer);
      perm?.removeEventListener('change', onPerm);
    };
  }, [check]);

  if (state === 'ok') return <>{children}</>;
  if (state === 'checking') return <main className="screen"><p>…</p></main>;

  const es = getLang() === 'es';
  const ios = isIOS();
  const steps = ios
    ? (es ? ['Abra Ajustes → Privacidad y seguridad → Localización → ACTIVADO.',
             'En la misma pantalla busque "Sitios web de Safari" → "Cuando se use la app" y active "Ubicación exacta".',
             'Vuelva a esta app y toque "Ya la activé".']
          : ['Open Settings → Privacy & Security → Location Services → ON.',
             'On the same screen find "Safari Websites" → "While Using the App" and turn ON "Precise Location".',
             'Come back to this app and tap "I turned it on".'])
    : (es ? ['Deslice desde arriba de la pantalla y active "Ubicación".',
             'Abra Ajustes → Apps → JW Express (o Chrome) → Permisos → Ubicación → Permitir.',
             'Vuelva a esta app y toque "Ya la activé".']
          : ['Swipe down from the top of the screen and turn ON "Location".',
             'Open Settings → Apps → JW Express (or Chrome) → Permissions → Location → Allow.',
             'Come back to this app and tap "I turned it on".']);
  return (
    <main className="screen">
      <h1>{es ? 'Active su ubicación' : 'Turn on your location'}</h1>
      <p><b>{es ? 'La aplicación no funciona con la ubicación apagada.' : 'The app does not work with location off.'}</b>{' '}
        {es ? 'Cada vez que se apaga queda registrado para la oficina.' : 'Every time it is turned off, it is recorded for the office.'}</p>
      <ol className="install-steps">{steps.map((s) => <li key={s}><p>{s}</p></li>)}</ol>
      <button className="btn primary big" onClick={() => { setState('checking'); void check(); }}>
        {es ? 'Ya la activé' : 'I turned it on'}
      </button>
      <p className="hint">{es ? 'Si no puede activarla, llame a la oficina.' : 'If you cannot turn it on, call the office.'}</p>
      <p className="hint" style={{ opacity: .6 }}>{why}</p>
    </main>
  );
}
